import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  Alert
} from '@mui/material';
import {
  Favorite,
  AttachMoney,
  LocalShipping,
  MedicalServices,
  Pets,
  VolunteerActivism
} from '@mui/icons-material';

interface Shelter {
  id: string;
  name: string;
  description?: string;
}

interface ShelterDonationsProps {
  shelter: Shelter;
}

const ShelterDonations: React.FC<ShelterDonationsProps> = ({ shelter }) => {
  const donationMethods = [
    {
      id: 'bank',
      title: 'Transfer bancar',
      description: 'Transfer direct în contul adapostului',
      icon: <AttachMoney />,
      action: 'Vezi datele bancare'
    },
    {
      id: 'online',
      title: 'Plată online',
      description: 'Donare rapidă prin card',
      icon: <Favorite />,
      action: 'Donează acum'
    },
    {
      id: 'materials',
      title: 'Materiale',
      description: 'Hrană, jucării, medicamente',
      icon: <LocalShipping />,
      action: 'Vezi lista necesități'
    }
  ];

  const needs = [
    { item: 'Hrană pentru câini', priority: 'high', quantity: '50kg' },
    { item: 'Hrană pentru pisici', priority: 'high', quantity: '30kg' },
    { item: 'Jucării pentru animale', priority: 'medium', quantity: '20 bucăți' },
    { item: 'Medicamente antiparazitare', priority: 'high', quantity: '10 cutii' },
    { item: 'Căști și lese', priority: 'medium', quantity: '15 bucăți' },
    { item: 'Litră pentru pisici', priority: 'low', quantity: '5 bucăți' }
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'high': return 'Prioritate mare';
      case 'medium': return 'Prioritate medie';
      case 'low': return 'Prioritate mică';
      default: return 'Normal';
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Susține {shelter.name}
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Fiecare donație contează!</strong> Contribuția ta ajută la îngrijirea animalelor 
          și la menținerea activității adapostului.
        </Typography>
      </Alert>

      <Box>
        {/* Metode de donare */}
        <Typography variant="h6" gutterBottom>
          Cum poți ajuta
        </Typography>
        
        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3} mb={4}>
          {donationMethods.map((method) => (
            <Box key={method.id} flex="1 1 300px">
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    {method.icon}
                    <Typography variant="h6">
                      {method.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {method.description}
                  </Typography>
                </CardContent>
                <Box sx={{ p: 2, pt: 0 }}>
                  <Button 
                    variant="contained" 
                    fullWidth
                    onClick={() => {
                      // TODO: Handle donation method
                      console.log('Donation method:', method.id);
                    }}
                  >
                    {method.action}
                  </Button>
                </Box>
              </Card>
            </Box>
          ))}
        </Box>

        {/* Necesități actuale */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Necesități actuale
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Iată ce are nevoie adapostul în acest moment:
          </Typography>
          
          <Box display="flex" flexWrap="wrap" gap={2}>
            {needs.map((need, index) => (
              <Box key={index} flex="1 1 250px" minWidth="250px">
                <Card variant="outlined">
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Typography variant="body1" fontWeight="medium">
                        {need.item}
                      </Typography>
                      <Chip 
                        label={getPriorityLabel(need.priority)}
                        color={getPriorityColor(need.priority)}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Cantitate: {need.quantity}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Informații despre donații */}
        <Paper elevation={1} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Despre donații
          </Typography>
          
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <MedicalServices color="primary" />
                <Typography variant="body1" fontWeight="medium">
                  Asistență medicală
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Donațiile ajută la acoperirea costurilor medicale pentru animalele 
                din adapost: vaccinuri, sterilizări, tratamente.
              </Typography>
            </Box>
            
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Pets color="primary" />
                <Typography variant="body1" fontWeight="medium">
                  Îngrijire zilnică
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Hrana, apa, jucăriile și alte necesități de bază pentru 
                bunăstarea animalelor.
              </Typography>
            </Box>
          </Box>
          
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3} mt={3}>
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <VolunteerActivism color="primary" />
                <Typography variant="body1" fontWeight="medium">
                  Voluntariat
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Dacă nu poți dona financiar, poți ajuta prin voluntariat 
                sau prin împărtășirea informațiilor despre adapost.
              </Typography>
            </Box>
            
            <Box flex={1}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Favorite color="primary" />
                <Typography variant="body1" fontWeight="medium">
                  Adopție responsabilă
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Cea mai bună formă de ajutor este adoptarea responsabilă 
                a unui animal din adapost.
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default ShelterDonations;
